import { useCallback } from 'react'
import { CalendarDays, ClipboardCopy, DoorOpen, FileText, Fingerprint, ListChecks, Timer } from 'lucide-react'
import { apiRead, usePermissions, useToast } from '../admin/primitives.js'
import {
  copierTexte, entreesLien, estPageCourante, useMenuContextuel, type ActionMenu, type GroupeMenu,
} from '../shell/menu-contextuel.js'
import { useGroupeFavori } from '../shell/menu-favori.js'
import { navigate } from '../router.js'
import { humanName } from './format.js'
import {
  CHEMIN_PASSAGES, CHEMIN_PLANNING, CHEMIN_VALIDATION, cheminCompteurs, cheminFiche, cheminFicheAcces,
  cheminPassages, cheminPlanningPersonne, cheminValidation, lireIdentiteAcces,
} from './menu-personne-modele.js'

/**
 * MENU CLIC DROIT D'UN SALARIÉ (lot 2, 24/10/2026) — navigation et copie ;
 * la seule écriture est l'épingle des favoris, celle de l'étoile.
 *
 * Chaque entrée reprend un lien qui existe déjà dans une page de Gestion,
 * sous le droit de la route qu'il ouvre :
 *
 *   · Ouvrir la fiche, nouvel onglet → /equipe/rh/salaries/:id                (person.read)
 *   · Valider ses heures             → /equipe/temps/validation?person=:id     (time.approve)
 *   · Voir ses compteurs             → /equipe/temps/compteurs/:id             (time.account.read.team ou .self)
 *   · Voir son planning              → /equipe/temps/planning?personne=:id     (time.schedule.read ;
 *                                      le lien « Voir son planning » de la fiche)
 *   · Voir sa fiche Access           → /securite/controle-acces/personnes/:idAccess  (access.view)
 *   · Voir ses passages aux portes   → /securite/controle-acces/historique?personne=  (access.history.view)
 *   · Ajouter aux favoris            → useGroupeFavori (shell/menu-favori.tsx), la page que
 *                                      « Ouvrir » ouvre, sous le même droit
 *   · Copier le nom, le lien, le matricule : des données déjà affichées
 *
 * Exclus tant qu'ils n'existent pas d'abord dans une page (carte du lot 2) :
 * ses pointages badgeuse, un message à une personne (reporté par Hugo le
 * 24/10/2026), une demande de disponibilités, et tout geste de porte.
 *
 * L'identifiant Access n'est pas celui de la fiche RH : il est relu au clic
 * (GET /api/v1/rh/people/:id, identité UNIFI_ACCESS ACTIVE), puis on navigue.
 * Pas de « nouvel onglet » vers ces deux pages : le navigateur bloque une
 * fenêtre ouverte après une lecture. Le serveur reste l'arbitre du
 * périmètre : une entrée affichée ne vaut jamais autorisation.
 */

export interface PersonneMenu {
  /** person.id (RH) — jamais l'identifiant UniFi Access. */
  id: string
  /** display_name tel que lu ; le menu le montre et le copie adouci (humanName). */
  nom: string
  /** Le matricule, quand l'écran l'affiche déjà. */
  matricule?: string | null | undefined
  /**
   * `unifi_linked` de la liste RH, ou l'identité déjà lue par la fiche :
   * `false` grise les entrées Access. Absent = inconnu, la lecture au clic
   * tranche.
   */
  accesLie?: boolean | undefined
  /**
   * La page que l'élément ouvre déjà. « Ouvrir », l'onglet et « Copier le
   * lien » la suivent, comme le menu du navigateur qu'ils remplacent : une
   * ligne de Compteurs mène aux compteurs, pas à la fiche.
   */
  lien?: 'fiche' | 'compteurs' | undefined
}

/**
 * Les déclencheurs du menu d'un salarié :
 *
 *     const menu = useMenuPersonne()
 *     <Link href={`/equipe/rh/salaries/${p.id}`} {...menu.personne({ id: p.id, nom: p.display_name })}>
 */
export function useMenuPersonne() {
  const menu = useMenuContextuel()
  const toast = useToast()
  const { permissions, loading } = usePermissions()
  // « Pas encore su » n'est pas « autorisé » : aucune entrée tant que les droits ne sont pas lus.
  const pret = !loading
  const peutFiche = pret && permissions.includes('person.read')
  const peutValider = pret && permissions.includes('time.approve')
  const peutCompteurs = pret && (permissions.includes('time.account.read.team') || permissions.includes('time.account.read.self'))
  const peutPlanning = pret && permissions.includes('time.schedule.read')
  // L'identité se lit dans la fiche RH : person.read, en plus du droit de la page Access ouverte.
  const peutFicheAcces = peutFiche && permissions.includes('access.view')
  const peutPassages = peutFiche && permissions.includes('access.history.view')
  const favori = useGroupeFavori()

  /** Relit l'identité d'accès, puis ouvre la page Access : jamais un identifiant RH dans une adresse Access. */
  const versAcces = useCallback(async (p: PersonneMenu, chemin: (accesId: string) => string) => {
    let r: { status: number; data: unknown }
    try {
      r = await apiRead<unknown>(`/api/v1/rh/people/${encodeURIComponent(p.id)}`)
    } catch {
      toast('error', 'Le serveur ne répond pas : l’identité d’accès n’a pas pu être lue.')
      return
    }
    const l = lireIdentiteAcces(r.status, r.data)
    if (!l.ok) { toast('error', l.message); return }
    navigate(chemin(l.id))
  }, [toast])

  const actions = useCallback((p: PersonneMenu): GroupeMenu[] => {
    const nom = humanName(p.nom)
    const fiche = cheminFiche(p.id)
    const compteurs = cheminCompteurs(p.id)
    const surCompteurs = p.lien === 'compteurs'
    const lien = surCompteurs
      ? (peutCompteurs ? entreesLien(compteurs, toast, { ouvrir: 'Voir ses compteurs' }) : [])
      : (peutFiche ? entreesLien(fiche, toast, { ouvrir: 'Ouvrir la fiche' }) : [])
    const ouvrir: ActionMenu[] = lien.filter((a) => a.id !== 'c-lien')
    if (surCompteurs && peutFiche && !estPageCourante(fiche)) {
      ouvrir.push({ id: 'fiche', libelle: 'Ouvrir la fiche', icone: <FileText size={15} />, href: fiche })
    }

    // Les pages qui ne lisent leur adresse qu'au montage : l'entrée disparaît quand on y est déjà.
    const temps: ActionMenu[] = []
    if (peutValider && !estPageCourante(CHEMIN_VALIDATION, { ignorerRecherche: true })) {
      temps.push({ id: 'valider', libelle: 'Valider ses heures', icone: <ListChecks size={15} />, href: cheminValidation(p.id) })
    }
    if (!surCompteurs && peutCompteurs && !estPageCourante(compteurs)) {
      temps.push({ id: 'compteurs', libelle: 'Voir ses compteurs', icone: <Timer size={15} />, href: compteurs })
    }
    if (peutPlanning && !estPageCourante(CHEMIN_PLANNING, { ignorerRecherche: true })) {
      temps.push({ id: 'planning', libelle: 'Voir son planning', icone: <CalendarDays size={15} />, href: cheminPlanningPersonne(p.id) })
    }

    const sansAcces = p.accesLie === false ? { indisponible: 'Aucune identité d’accès liée' } : {}
    const acces: ActionMenu[] = []
    if (peutFicheAcces) {
      acces.push({ id: 'acces', libelle: 'Voir sa fiche Access', icone: <Fingerprint size={15} />, ...sansAcces, agir: () => versAcces(p, cheminFicheAcces) })
    }
    if (peutPassages && !estPageCourante(CHEMIN_PASSAGES, { ignorerRecherche: true })) {
      acces.push({ id: 'passages', libelle: 'Voir ses passages aux portes', icone: <DoorOpen size={15} />, ...sansAcces, agir: () => versAcces(p, cheminPassages) })
    }

    const copier: ActionMenu[] = [
      { id: 'c-nom', libelle: 'Copier le nom', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(nom, 'nom', toast) },
      ...lien.filter((a) => a.id === 'c-lien'),
    ]
    const matricule = p.matricule?.trim()
    if (matricule) {
      copier.push({ id: 'c-matricule', libelle: 'Copier le matricule', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(matricule, 'matricule', toast) })
    }

    // Le favori suit « Ouvrir » : la fiche, ou les compteurs sur une ligne de Compteurs.
    const epingle: GroupeMenu = surCompteurs
      ? (peutCompteurs ? favori(compteurs, `Compteurs — ${nom}`) : { actions: [] })
      : (peutFiche ? favori(fiche, nom) : { actions: [] })

    return [
      { actions: ouvrir },
      { titre: 'Temps', actions: temps },
      { titre: 'Accès', actions: acces },
      epingle,
      { titre: 'Copier', actions: copier },
    ]
  }, [peutFiche, peutValider, peutCompteurs, peutPlanning, peutFicheAcces, peutPassages, toast, versAcces, favori])

  return {
    personne: (p: PersonneMenu) => menu(() => actions(p), humanName(p.nom)),
  }
}
